/**
 * Encryption settings.
 *
 * Entirely originated — the design has no encryption UI at all. It exists to
 * answer three questions a user of a zero-access product will actually have:
 * what is protecting my files, what does Mauvely know, and what happens if I
 * lose my password.
 *
 * The "what we can see" section is deliberately blunt. A product that claims
 * end-to-end encryption and stays vague about its metadata is doing the
 * marketing version of the claim; listing the leakage is the honest one, and it
 * matches docs/SECURITY.md line for line.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type PasskeyWire } from '../api';
import { ds } from '../ds';
import { useApp } from '../state';
import { Panel, PageTitle, Eyebrow } from '../components/Shell';
import { readableFingerprint, WrongSecretError } from '../crypto/keys';
import { enrolPasskey, prfSupported } from '../crypto/passkey';
import { Devices } from '../components/Devices';
import { AUTO_LOCK_MS, isDeviceTrusted, signOut } from '../crypto/vault';
import { formatBytes, monoPlain } from '../theme';
import { useT } from '../i18n';

export function Settings() {
  const t = useT();
  const { Button, Icon } = ds();
  const { me, storage, lockVault } = useApp();

  const [trusted, setTrusted] = useState(false);
  useEffect(() => {
    void isDeviceTrusted().then(setTrusted);
  }, []);

  return (
    <div style={{ display: 'grid', gap: 24, maxWidth: 780 }}>
      <PageTitle title={t('settings.encryption')} />

      <Panel style={{ padding: '24px 26px', display: 'grid', gap: 16 }}>
        <Eyebrow>{t('settings.your-keys')}</Eyebrow>

        <Row label={t('settings.encryption-2')} value="AES-256-GCM" />
        <Row
          label={t('settings.key-derivation')}
          value="Argon2id · 64 MiB, 3 passes"
        />
        <Row
          label={t('settings.your-public-key')}
          value={
            me?.publicKeyFingerprint ? readableFingerprint(me.publicKeyFingerprint) : 'unknown'
          }
          hint={t('settings.read-this-to-someone-before')}
        />
        <Row
          label={t('settings.storage')}
          value={
            !storage
              ? '—'
              : // No ceiling. "0 of -1 bytes · 0%" is what the other branch
                // would print, so the sentinel is checked rather than formatted.
                storage.limitMb < 0
                ? `${formatBytes(storage.usedBytes)} stored`
                : `${formatBytes(storage.usedBytes)} of ${formatBytes(storage.limitBytes)} · ${storage.percent}%`
          }
        />
      </Panel>

      <Panel style={{ padding: '24px 26px', display: 'grid', gap: 14 }}>
        <Eyebrow>{t('settings.this-device')}</Eyebrow>
        <div style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.55 }}>
          {trusted
            ? 'Your key is stored on this device so you stay unlocked.'
            : 'Your key is only in memory. You will be asked for your password again next time.'}
        </div>
        {!trusted && (
          <div style={monoPlain}>
            Locks itself after {Math.round(AUTO_LOCK_MS / 60000)} minutes without activity
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" size="sm" onClick={lockVault}>
            {t('settings.lock-now')}
          </Button>
          {trusted && (
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                await signOut();
                setTrusted(false);
              }}
            >
              {t('settings.forget-this-device')}
            </Button>
          )}
        </div>
      </Panel>

      <Panel style={{ padding: '24px 26px', display: 'grid', gap: 14 }}>
        <Eyebrow>{t('settings.recovery-phrase')}</Eyebrow>

        {me && !me.recoveryConfirmed && (
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 12,
              background: 'var(--danger-100)',
              color: 'var(--danger-600)',
              fontSize: 13.5,
              lineHeight: 1.5
            }}
          >
            <Icon name="alert-triangle" size={14} /> You have not confirmed your recovery
            phrase.
          </div>
        )}

        <div style={{ fontSize: 14, color: 'var(--text-body)', lineHeight: 1.55 }}>
          {t('settings.phrase-moving')}
        </div>
      </Panel>

      <Passkeys />
      <Devices />
    </div>
  );
}

/**
 * Passkeys as a third way to unlock.
 *
 * The whole section hides itself where the browser cannot do PRF, rather than
 * showing a disabled control: there is nothing the user could do about it, and
 * the password path they are already using is unaffected.
 *
 * The copy is blunt about the tradeoff on purpose. A synced passkey is
 * genuinely convenient and genuinely moves part of the vault's security to
 * whoever controls the platform account holding it. Saying so is the same
 * commitment the rest of this screen makes about metadata.
 */
function Passkeys() {
  const t = useT();
  const { Button, Icon, Input } = ds();
  const { me } = useApp();

  const [supported, setSupported] = useState<boolean | null>(null);
  const [rows, setRows] = useState<PasskeyWire[]>([]);
  const [password, setPassword] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { passkeys } = await api.vault.passkeys.list();
      setRows(passkeys);
    } catch {
      // Not fatal — the section simply shows nothing enrolled.
    }
  }, []);

  useEffect(() => {
    void (async () => {
      const ok = await prfSupported();
      setSupported(ok);
      if (ok) await load();
    })();
  }, [load]);

  if (supported !== true) return null;

  const enrol = async () => {
    if (!me) return;
    setBusy(true);
    setError(null);
    try {
      const blobs = await api.vault.get();
      const enrolled = await enrolPasskey({ password }, blobs, {
        id: me.user.id,
        email: me.user.email,
        name: me.user.name
      });

      if (!enrolled) {
        setError(
          'That device created a passkey but would not derive a key from it.'
        );
        return;
      }

      await api.vault.passkeys.enrol({ ...enrolled, label: label.trim() || undefined });
      setPassword('');
      setLabel('');
      await load();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') return;
      setError(
        err instanceof WrongSecretError
          ? 'That password does not open your vault.'
          : err instanceof ApiError
            ? err.message
            : 'Could not add that passkey.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel style={{ padding: '24px 26px', display: 'grid', gap: 14 }}>
      <Eyebrow>{t('settings.unlock-with-a-passkey')}</Eyebrow>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((p) => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Icon name="key" size={16} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
                  {p.label || 'Passkey'}
                </div>
                <div style={monoPlain}>
                  added {new Date(p.createdAt * 1000).toLocaleDateString()}
                  {p.lastUsedAt
                    ? ` · last used ${new Date(p.lastUsedAt * 1000).toLocaleDateString()}`
                    : ' · not used yet'}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  void (async () => {
                    setBusy(true);
                    try {
                      await api.vault.passkeys.remove(p.id);
                      await load();
                    } finally {
                      setBusy(false);
                    }
                  })();
                }}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180, maxWidth: 260 }}>
          <Input
            label={t('settings.confirm-your-password')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword((e.target as HTMLInputElement).value)}
          />
        </div>
        <div style={{ flex: 1, minWidth: 160, maxWidth: 220 }}>
          <Input
            label={t('settings.name-this-device')}
            value={label}
            onChange={(e) => setLabel((e.target as HTMLInputElement).value)}
            placeholder={t('settings.work-laptop')}
          />
        </div>
        <Button variant="secondary" size="md" disabled={busy || !password} onClick={() => void enrol()}>
          {busy ? 'Working…' : 'Add a passkey'}
        </Button>
      </div>

      {error && <div style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error}</div>}
    </Panel>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ display: 'grid', gap: 3 }}>
      <span style={monoPlain}>{label}</span>
      <span style={{ fontSize: 14, color: 'var(--text-strong)', wordBreak: 'break-word' }}>
        {value}
      </span>
      {hint && (
        <span style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>{hint}</span>
      )}
    </div>
  );
}
